import { FC, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Input,
  Textarea,
  Checkbox,
  Field,
  Accordion,
} from '@chakra-ui/react';
import { LuArrowLeft, LuSave } from 'react-icons/lu';
import {
  AIPrompt,
  AIPromptInput,
  AIPromptHistory,
  PromptWeights,
  InbreedingThresholds,
  DEFAULT_PROMPT_WEIGHTS,
  DEFAULT_INBREEDING_THRESHOLDS,
  validateWeightsTotal,
  validateThresholdsOrder,
} from '@/types/aiManagement';
import { aiPromptService } from '@/services/firebase/aiPromptService';
import {
  WeightSlidersGroup,
  ThresholdInput,
  PromptPreview,
  VersionHistory,
} from '@/components/aiPrompt';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';

/**
 * プロンプト編集ページ（新規作成・編集共用）
 */
export const AIPromptEditPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const isNew = !id || id === 'new';
  const fetchedRef = useRef(false);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // フォーム状態
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [weights, setWeights] = useState<PromptWeights>(DEFAULT_PROMPT_WEIGHTS);
  const [thresholds, setThresholds] = useState<InbreedingThresholds>(
    DEFAULT_INBREEDING_THRESHOLDS
  );
  const [customInstructions, setCustomInstructions] = useState('');
  const [changeNote, setChangeNote] = useState('');

  // 既存プロンプトの情報
  const [currentPrompt, setCurrentPrompt] = useState<AIPrompt | null>(null);
  const [histories, setHistories] = useState<AIPromptHistory[]>([]);

  useEffect(() => {
    if (!isNew && id && user && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchPrompt();
    }
  }, [id, user, isNew]);

  const fetchPrompt = async () => {
    try {
      setLoading(true);
      const prompt = await aiPromptService.getPromptById(id!);
      if (!prompt) {
        alert('プロンプトが見つかりません');
        navigate('/admin/prompts');
        return;
      }

      setCurrentPrompt(prompt);
      setName(prompt.name);
      setDescription(prompt.description);
      setIsDefault(prompt.isDefault);
      setWeights(prompt.weights);
      setThresholds(prompt.inbreedingThresholds);
      setCustomInstructions(prompt.customInstructions || '');

      // 履歴取得
      const historyData = await aiPromptService.getPromptHistory(id!, user!.uid);
      setHistories(historyData);
    } catch (error) {
      console.error('プロンプト取得エラー:', error);
      alert('プロンプトの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    // バリデーション
    if (!name.trim()) {
      alert('プロンプト名を入力してください');
      return;
    }

    if (!validateWeightsTotal(weights)) {
      alert('重み付けの合計が100%になっていません');
      return;
    }

    if (!validateThresholdsOrder(thresholds)) {
      alert('閾値の順序が正しくありません');
      return;
    }

    try {
      setSaving(true);

      if (isNew) {
        // 新規作成
        const data: AIPromptInput = {
          userId: user.uid,
          name: name.trim(),
          description: description.trim(),
          version: 1,
          isActive: true,
          isDefault,
          weights,
          inbreedingThresholds: thresholds,
          customInstructions: customInstructions.trim() || undefined,
        };

        await aiPromptService.createPrompt(data);
      } else {
        // 更新
        await aiPromptService.updatePrompt(
          id!,
          {
            name: name.trim(),
            description: description.trim(),
            isDefault,
            weights,
            inbreedingThresholds: thresholds,
            customInstructions: customInstructions.trim() || undefined,
          },
          changeNote.trim() || undefined
        );
      }

      navigate('/admin/prompts');
    } catch (error) {
      console.error('保存エラー:', error);
      alert(error instanceof Error ? error.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <VStack gap={4} py={8}>
          <Spinner size="xl" />
          <Text>読み込み中...</Text>
        </VStack>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <VStack align="stretch" gap={6}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <HStack gap={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/prompts')}
            >
              <LuArrowLeft />
            </Button>
            <Text fontSize="2xl" fontWeight="bold">
              {isNew ? '新規プロンプト作成' : 'プロンプト編集'}
            </Text>
          </HStack>
          <Button
            colorPalette="blue"
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            <LuSave />
            保存
          </Button>
        </HStack>

        {/* 基本情報 */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <Text fontWeight="bold" fontSize="lg" mb={4}>
            基本情報
          </Text>
          <VStack align="stretch" gap={4}>
            <Field.Root required>
              <Field.Label>プロンプト名</Field.Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例: 標準交配提案"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>説明</Field.Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="このプロンプトの用途や特徴を記述"
                rows={2}
              />
            </Field.Root>

            <Checkbox.Root
              checked={isDefault}
              onCheckedChange={(e) => setIsDefault(!!e.checked)}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>デフォルトに設定</Checkbox.Label>
            </Checkbox.Root>
          </VStack>
        </Box>

        {/* 評価基準の重み付け */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <Text fontWeight="bold" fontSize="lg" mb={4}>
            評価基準の重み付け
          </Text>
          <Text fontSize="sm" color="gray.600" mb={4}>
            AIが交配相手を評価する際の重み付けを調整します。
            スライダーを動かすと他の値も自動的に調整されます。
          </Text>
          <WeightSlidersGroup
            weights={weights}
            onChange={setWeights}
            autoBalance={true}
          />
        </Box>

        {/* 近親係数の閾値設定 */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <Text fontWeight="bold" fontSize="lg" mb={4}>
            近親係数の閾値設定
          </Text>
          <ThresholdInput thresholds={thresholds} onChange={setThresholds} />
        </Box>

        {/* カスタム指示 */}
        <Accordion.Root collapsible>
          <Accordion.Item value="custom">
            <Accordion.ItemTrigger>
              <Box flex={1} textAlign="left">
                <Text fontWeight="bold">カスタム指示（上級者向け）</Text>
              </Box>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent>
              <Box bg="white" p={6} borderRadius="lg" shadow="sm">
                <Text fontSize="sm" color="gray.600" mb={4}>
                  AIに対する追加の指示を自由記述できます。
                  特定の条件や注意事項がある場合に使用してください。
                </Text>
                <Textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="例: 小型犬との交配は避けてください"
                  rows={4}
                />
              </Box>
            </Accordion.ItemContent>
          </Accordion.Item>
        </Accordion.Root>

        {/* プレビュー */}
        <Accordion.Root collapsible defaultValue={['preview']}>
          <Accordion.Item value="preview">
            <Accordion.ItemTrigger>
              <Box flex={1} textAlign="left">
                <Text fontWeight="bold">プロンプトプレビュー</Text>
              </Box>
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
            <Accordion.ItemContent>
              <Box bg="white" p={6} borderRadius="lg" shadow="sm">
                <PromptPreview
                  weights={weights}
                  thresholds={thresholds}
                  customInstructions={customInstructions}
                />
              </Box>
            </Accordion.ItemContent>
          </Accordion.Item>
        </Accordion.Root>

        {/* 変更理由（編集時のみ） */}
        {!isNew && (
          <Box bg="white" p={6} borderRadius="lg" shadow="sm">
            <Field.Root>
              <Field.Label>変更理由（任意）</Field.Label>
              <Input
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="例: 重み付けを調整しました"
              />
              <Field.HelperText>
                履歴に記録されます
              </Field.HelperText>
            </Field.Root>
          </Box>
        )}

        {/* バージョン履歴（編集時のみ） */}
        {!isNew && histories.length > 0 && (
          <Box bg="white" p={6} borderRadius="lg" shadow="sm">
            <Text fontWeight="bold" fontSize="lg" mb={4}>
              バージョン履歴
            </Text>
            <VersionHistory
              histories={histories}
              currentVersion={currentPrompt?.version || 1}
            />
          </Box>
        )}
      </VStack>
    </AppLayout>
  );
};

export default AIPromptEditPage;
